import React from "react";
import logError from "../../utils/logError";

/**
 * GRANICA BLEDU — zamienia biala strone na czytelny komunikat.
 *
 * DLACZEGO POWSTALA
 * -----------------
 * W calym projekcie nie bylo ANI JEDNEJ granicy bledu (sprawdzone: zero
 * wystapien `componentDidCatch` i `getDerivedStateFromError`). React 16 w
 * takim ukladzie nie ma gdzie zatrzymac wyjatku, wiec przy pierwszym bledzie
 * w `render` albo w metodzie cyklu zycia **odmontowuje cale drzewo** —
 * znika ekran, znika menu, zostaje pusty `#root`. Uzytkownik widzi biala
 * strone i nie ma z czym przyjsc do programisty.
 *
 * Tak wygladalo zgloszenie "raport tras wywala strone": pusta biala karta
 * pod adresem `/route-report`, bez naglowka i bez menu. Sam objaw nie mowi
 * nic o przyczynie — a przyczyna byla wypisana w konsoli, gdzie nikt nie
 * patrzy.
 *
 * CO SIE ZMIENIA
 * --------------
 * Blad zatrzymuje sie na granicy ekranu. Naglowek, menu i pozostale ekrany
 * zyja dalej, a w miejscu wywroconego ekranu pojawia sie komunikat z trescia
 * bledu i z drzewem komponentow. To wystarczy, zeby zrobic zrzut ekranu i
 * wiedziec, ktory komponent sie wywrocil — bez odtwarzania awarii u siebie.
 *
 * DLACZEGO TEN KOMPONENT NIE UZYWA ANTD ANI SCSS
 * ----------------------------------------------
 * Bo rysuje sie WLASNIE WTEDY, gdy cos w aplikacji jest zepsute. Gdyby
 * zastepczy widok siegal po te same biblioteki i style co ekran, ktory
 * wlasnie padl, mialby szanse padnac razem z nim i znowu zostalaby biala
 * strona. Dlatego tu sa tylko zwykle znaczniki i style w atrybucie.
 *
 * CZEGO TA GRANICA NIE LAPIE
 * --------------------------
 * Bledow spoza rysowania: odrzuconych obietnic, wyjatkow w `setTimeout` i w
 * procedurach obslugi zdarzen. Tamte nie przechodza przez drzewo Reacta i
 * trzeba je lapac na miejscu (tak jak `findAddress` na telemetrii).
 */

type GranicaBleduProps = {
    /** Sciezka ekranu — trafia do komunikatu i do logu w konsoli. */
    sciezka?: string;
    children?: React.ReactNode;
};

type GranicaBleduState = {
    blad: Error | null;
    /** Drzewo komponentow w chwili bledu — z `componentDidCatch`. */
    stosKomponentow: string;
};

const styleRamki: React.CSSProperties = {
    margin: "1.5rem",
    padding: "1.25rem 1.5rem",
    background: "#fff",
    border: "1px solid #f0f2f5",
    borderLeft: "4px solid #c0392b",
    borderRadius: 2,
    fontFamily: "inherit",
    color: "#262626",
};

const styleTresci: React.CSSProperties = {
    margin: "0.75rem 0 0",
    padding: "0.75rem",
    background: "#fafafa",
    border: "1px solid #f0f2f5",
    fontSize: "12px",
    fontFamily: "Consolas, Menlo, monospace",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    maxHeight: "18rem",
    overflow: "auto",
};

const stylePrzycisku: React.CSSProperties = {
    marginRight: "0.5rem",
    padding: "0.35rem 0.9rem",
    border: "1px solid #d9d9d9",
    background: "#fff",
    borderRadius: 2,
    cursor: "pointer",
    fontSize: "13px",
};

class GranicaBledu extends React.Component<GranicaBleduProps, GranicaBleduState> {
    state: GranicaBleduState = { blad: null, stosKomponentow: "" };

    static getDerivedStateFromError(blad: Error): Partial<GranicaBleduState> {
        // Wywolywane w fazie rysowania — wolno tu tylko oddac nowy stan.
        return { blad };
    }

    componentDidCatch(blad: Error, info: React.ErrorInfo): void {
        const gdzie = this.props.sciezka ? "granica bledu — " + this.props.sciezka : "granica bledu";
        logError(gdzie, blad);
        const stos = info && info.componentStack ? info.componentStack : "";
        if (stos) {
            // Osobno od `logError`: tamto pokazuje stos wywolan JavaScriptu,
            // a to mowi, ktory komponent w drzewie byl rysowany.
            console.error("[mygps] Drzewo komponentow w chwili bledu:" + stos);
        }
        this.setState({ stosKomponentow: stos });
    }

    ponow = (): void => {
        // Czyszczenie stanu granicy montuje ekran od nowa. Pomaga, gdy blad
        // wynikal z chwilowego stanu (np. odpowiedzi, ktora jeszcze nie
        // doszla), a nie z samego kodu.
        this.setState({ blad: null, stosKomponentow: "" });
    };

    /*
     * NAJWAZNIEJSZY FAKT NA WIDOKU, RESZTA POD TROJKATEM.
     *
     * Sama tresc bledu ("Cannot read properties of undefined (reading
     * 'map')") nie mowi, KTORY komponent sie wywrocil — a w drzewie tego
     * ekranu jest kilkanascie miejsc z `.map`. Pierwsza linia stosu
     * komponentow podaje to jednoznacznie, wiec idzie na widok, bez
     * rozwijania szczegolow. Caly stos zostaje pod trojkatem, zeby
     * komunikat nie rozlewal sie na pol ekranu.
     */
    nazwaKomponentu(): string {
        // Znak nowej linii przez kod, nie przez sekwencje ucieczki — plik byl
        // generowany skryptem i podwojne ucieczki juz raz go uszkodzily.
        const NOWA_LINIA = String.fromCharCode(10);
        const linie = this.state.stosKomponentow
            .split(NOWA_LINIA)
            .map(l => l.trim())
            .filter(Boolean);
        if (linie.length === 0) return "";
        // React zapisuje pierwsza linie jako "in Nazwa (at plik:linia)".
        const pierwsza = linie[0];
        const bezPrzedrostka = pierwsza.indexOf("in ") === 0 ? pierwsza.slice(3) : pierwsza;
        const nawias = bezPrzedrostka.indexOf(" (");
        return nawias > 0 ? bezPrzedrostka.slice(0, nawias) : bezPrzedrostka;
    }

    render(): React.ReactNode {
        const { blad, stosKomponentow } = this.state;
        if (!blad) return this.props.children;

        const tresc = blad.message ? blad.message : String(blad);
        const stosJs = blad.stack ? blad.stack : "";
        const komponent = this.nazwaKomponentu();

        return (
            <div style={styleRamki} role="alert">
                <div style={{ fontSize: "16px", fontWeight: 600, marginBottom: "0.35rem" }}>
                    Ten ekran się nie narysował
                </div>
                <div style={{ fontSize: "13px", color: "#595959" }}>
                    Błąd zatrzymał rysowanie ekranu
                    {this.props.sciezka ? <code> {this.props.sciezka}</code> : null}.
                    Pozostałe ekrany i menu działają normalnie.
                    <strong> Pokaż ten komunikat programiście</strong> — jest w nim miejsce, które się wywróciło.
                </div>
                <pre style={styleTresci}>{tresc}</pre>
                {komponent &&
                    <div style={{ fontSize: "13px", marginTop: "0.5rem" }}>
                        Wywrócił się komponent: <code style={{ fontWeight: 600 }}>{komponent}</code>
                    </div>
                }
                {(stosJs || stosKomponentow) &&
                    <details style={{ marginTop: "0.75rem", fontSize: "13px" }}>
                        <summary style={{ cursor: "pointer" }}>Szczegóły techniczne</summary>
                        <pre style={styleTresci}>{stosJs + (stosKomponentow ? "\n\nDrzewo komponentów:" + stosKomponentow : "")}</pre>
                    </details>
                }
                <div style={{ marginTop: "1rem" }}>
                    <button type="button" style={stylePrzycisku} onClick={this.ponow}>Spróbuj ponownie</button>
                    <button type="button" style={stylePrzycisku} onClick={() => window.history.back()}>Wróć</button>
                    <button type="button" style={stylePrzycisku} onClick={() => window.location.reload()}>Odśwież stronę</button>
                </div>
            </div>
        );
    }
}

export default GranicaBledu;
